var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Sutl } from '../../../sutl';
import { Timeouts } from './framework/Timeouts';
export class Epiphan {
    constructor(http, state, log) {
        this.http = http;
        this.state = state;
        this.log = log;
        // other class properties and constructor
        this.authToken = '';
        this.configuredCodes = [];
        this.detectedVideoInputs = [];
        this.recorderCodes = [];
        this.streamerCodes = [];
        this.timeouts = new Timeouts();
        this.stateUpdateTimeouts = {};
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.detected = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return { videoInputs: this.detectedVideoInputs };
            }),
            recomputeCapsOnChange: true,
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const value = yield this.getParam('firmware_version');
                return value === '' ? 'unknown' : value;
            }),
        };
        this.mediaOperation = {
            set: (key, value, tags) => __awaiter(this, void 0, void 0, function* () {
                // The Epiphan takes some time to update its recording and streaming state. In order to
                // provide a smooth user experience, we immediately change the state (assuming the
                // command will be successful) and suspend polling until the Epiphan has time to reflect
                // the correct state.
                if (value.operation === 'mediaStreamerStop') {
                    yield this.setParam('publish_enabled', key, 'off');
                    this.state.mediaStreamerState.update(key, 'stopped');
                    this.suspendUpdate(key, 'streaming');
                }
                else if (value.operation === 'mediaStreamerStream') {
                    yield this.setParam('publish_enabled', key, 'on');
                    this.state.mediaStreamerState.update(key, 'streaming');
                    this.suspendUpdate(key, 'streaming');
                }
                else if (value.operation === 'mediaRecorderRecord') {
                    yield this.setParam('rec_enabled', key, 'on');
                    this.state.mediaRecorderState.update(key, 'recording');
                    this.suspendUpdate(key, 'recording');
                }
                else if (value.operation === 'mediaRecorderStop') {
                    yield this.setParam('rec_enabled', key, 'off');
                    this.state.mediaRecorderState.update(key, 'stopped');
                    this.suspendUpdate(key, 'recording');
                }
                return value;
            }),
        };
        this.mediaOperations = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const ops = {};
                for (const code of this.recorderCodes) {
                    ops[code] = ['mediaRecorderRecord', 'mediaRecorderStop'];
                }
                for (const code of this.streamerCodes) {
                    if (!(code in ops))
                        ops[code] = [];
                    ops[code].push('mediaStreamerStream', 'mediaStreamerStop');
                }
                return ops;
            }),
        };
        this.mediaRecorderState = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const state = this.state.mediaRecorderState.values[key];
                if (state === undefined) {
                    this.log.error(`no mediaRecorderState for ${key}`);
                    return 'stopped';
                }
                return state;
            }),
            getKeys: () => this.configuredCodes.filter(code => this.recorderCodes.includes(code)),
        };
        this.mediaStreamerState = {
            get: (key, tags) => __awaiter(this, void 0, void 0, function* () {
                const state = this.state.mediaStreamerState.values[key];
                if (state === undefined) {
                    this.log.error(`no mediaStreamerState for ${key}`);
                    return 'stopped';
                }
                return state;
            }),
            getKeys: () => this.configuredCodes.filter(code => this.streamerCodes.includes(code)),
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                var _a;
                return (_a = this.currentInputCode) !== null && _a !== void 0 ? _a : '';
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                this.currentInputCode = code;
                return code;
            }),
        };
        // polling
        this.polls = [
            {
                intervalMs: 4000,
                waitUntilDone: true,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    const videoInputs = yield this.getVideoInputs();
                    yield this.getAllStates();
                    this.state.detected.update({ videoInputs });
                }),
            },
        ];
    }
    // phase callbacks
    onConfigure(config) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.destroyAll();
            this.stateUpdateTimeouts = {};
            // The Epiphan doesn't have a video switcher, but we need to track which video input is
            // selected by Inara. If we've previously assigned the video input, and it is still present
            // in the config, then we leave it alone. Otherwise, we pick one from the config.
            this.configuredCodes = Object.values((_a = config.videoInputs) !== null && _a !== void 0 ? _a : {}).map(v => v.code);
            if (this.configuredCodes.length > 0 &&
                (this.currentInputCode === undefined ||
                    !this.configuredCodes.includes(this.currentInputCode))) {
                this.currentInputCode = this.configuredCodes.sort()[0];
            }
            this.detectedVideoInputs = yield this.getVideoInputs();
            yield this.getAllStates();
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.destroyAll();
        });
    }
    onInitialize(credentials) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const username = (_a = credentials.username) !== null && _a !== void 0 ? _a : 'admin';
            const password = (_b = credentials.password) !== null && _b !== void 0 ? _b : '';
            this.authToken = btoa(`${username}${password === '' ? '' : ':' + password}`);
            try {
                const response = yield this.httpGet(`/admin/get_params.cgi?product_name`);
                if (response.error) {
                    return 'authFailed';
                }
                else if ((_c = response.data) === null || _c === void 0 ? void 0 : _c.startsWith('product_name')) {
                    return 'connected';
                }
                else {
                    return 'verifyFailed';
                }
            }
            catch (error) {
                return 'connectFailed';
            }
            return 'connectFailed';
        });
    }
    // other class methods
    getAllStates() {
        return __awaiter(this, void 0, void 0, function* () {
            yield Sutl.forEachAsyncSerial(this.streamerCodes, (code) => __awaiter(this, void 0, void 0, function* () {
                if (this.configuredCodes.includes(code) && !this.isSuspended(code, 'streaming')) {
                    const value = yield this.getParam('publish_enabled', code);
                    this.state.mediaStreamerState.update(code, value === 'on' ? 'streaming' : 'stopped');
                }
            }));
            yield Sutl.forEachAsyncSerial(this.recorderCodes, (code) => __awaiter(this, void 0, void 0, function* () {
                if (this.configuredCodes.includes(code) && !this.isSuspended(code, 'recording')) {
                    const value = yield this.getParam('rec_enabled', code);
                    this.state.mediaRecorderState.update(code, value === 'on' ? 'recording' : 'stopped');
                }
            }));
        });
    }
    getParam(name, channel) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const path = channel
                ? `/admin/${channel}/get_params.cgi?${name}`
                : `/admin/get_params.cgi?${name}`;
            const response = yield this.httpGet(path);
            if (response.data) {
                const values = response.data.trim().split(/\s*=\s*/);
                if (values[0] === name)
                    return (_a = values[1]) !== null && _a !== void 0 ? _a : '';
            }
            throw new Error(`invalid response for ${name}: ${response.data}`);
        });
    }
    getVideoInputs() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const recorderCodes = [];
            const streamerCodes = [];
            const videoInputs = [];
            const channels = (yield this.httpGetJson(`/api/channels`));
            if (channels.status === 'ok' && Array.isArray(channels.result)) {
                for (const result of channels.result) {
                    if (result.id === undefined) {
                        this.log.error(`missing id for channel result`);
                    }
                    else {
                        const code = `channel${result.id}`;
                        streamerCodes.push(code);
                        videoInputs.push({
                            code,
                            name: (_a = result.name) !== null && _a !== void 0 ? _a : `Channel ${result.id}`,
                        });
                    }
                }
            }
            else {
                this.log.error(`invalid channels response`);
            }
            const recorders = (yield this.httpGetJson(`/api/recorders`));
            if (recorders.status === 'ok' && Array.isArray(recorders.result)) {
                for (const result of recorders.result) {
                    if (result.id === undefined) {
                        this.log.error(`missing id for recorder result`);
                    }
                    else {
                        const code = `channel${result.id}`;
                        recorderCodes.push(code);
                        // The channels also show up in the streamers, so don't add them twice.
                        if (videoInputs.find(vi => vi.code === code))
                            continue;
                        videoInputs.push({
                            code,
                            name: (_b = result.name) !== null && _b !== void 0 ? _b : `Channel ${result.id}`,
                        });
                    }
                }
            }
            else {
                this.log.error(`invalid recorders response`);
            }
            this.recorderCodes = recorderCodes;
            this.streamerCodes = streamerCodes;
            return videoInputs;
        });
    }
    httpGet(path) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.http.request('get', path, {
                headers: { Authorization: `Basic ${this.authToken}` },
            });
        });
    }
    httpGetJson(path) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.http.requestJson('get', path, {
                headers: { Authorization: `Basic ${this.authToken}` },
            });
        });
    }
    isSuspended(channel, type) {
        const key = `${channel}.${type}`;
        return this.stateUpdateTimeouts[key] !== undefined;
    }
    setParam(name, channel, value) {
        return __awaiter(this, void 0, void 0, function* () {
            const path = `/admin/${channel}/set_params.cgi?${name}=${value}`;
            yield this.httpGet(path);
        });
    }
    suspendUpdate(channel, type) {
        const key = `${channel}.${type}`;
        if (this.stateUpdateTimeouts[key] === undefined) {
            this.stateUpdateTimeouts[key] = this.timeouts.create();
        }
        this.stateUpdateTimeouts[key].set(() => {
            this.stateUpdateTimeouts[key].destroy();
            delete this.stateUpdateTimeouts[key];
        }, 6000);
    }
}
// static properties
Epiphan.driverType = 'httpDriver';
Epiphan.key = '-MYjPO9d_ZoSHkztys0H';
Epiphan.httpRequestOptions = {
    prettyCommands: false,
    scheme: 'http',
};
